import React, { useState } from 'react';
import { Calendar, CheckCircle, Clock, AlertCircle, ArrowUpDown, Banknote } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';


const PayoutTracking = ({ payouts, loading }) => {
  const [sortField, setSortField] = useState('payout_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('all');

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 })?.format(val || 0);

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr || '—';
    }
  };

  const getDaysUntil = (dateStr) => {
    try {
      return differenceInDays(parseISO(dateStr), new Date());
    } catch {
      return null;
    }
  };

  const statusConfig = {
    paid: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Paid' },
    scheduled: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Scheduled' },
    processing: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Processing' },
    failed: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' },
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filteredPayouts = payouts
    ?.filter((p) => filter === 'all' || p?.status === filter)
    ?.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];
      if (sortField === 'payout_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (sortField === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }) || [];

  const upcomingPayouts = payouts?.filter((p) => p?.status === 'scheduled') || [];
  const totalPaid = payouts?.filter((p) => p?.status === 'paid')?.reduce((s, p) => s + Number(p?.amount || 0), 0) || 0;
  const totalScheduled = upcomingPayouts?.reduce((s, p) => s + Number(p?.amount || 0), 0) || 0;

  const SortButton = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 text-xs text-slate-400 font-medium hover:text-[#1a2744] transition-colors"
    >
      {label}
      <ArrowUpDown size={10} className={sortField === field ? 'text-[#c9a84c]' : ''} />
    </button>
  );

  if (loading) {
    return (
      <section id="payouts" className="mb-16">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Payout Tracking
          </h2>
        </div>
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      </section>
    );
  }

  return (
    <section id="payouts" className="mb-16">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Payout Tracking
        </h2>
        <p className="text-slate-500 text-sm">Monitor your payment history and upcoming distributions</p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#1a2744] rounded-2xl p-5">
          <div className="text-white/60 text-xs mb-1">Total Paid Out</div>
          <div className="text-white text-2xl font-bold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {formatCurrency(totalPaid)}
          </div>
          <div className="text-white/40 text-xs mt-1">{payouts?.filter((p) => p?.status === 'paid')?.length || 0} payments</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="text-slate-400 text-xs mb-1">Upcoming Payouts</div>
          <div className="text-[#c9a84c] text-2xl font-bold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {formatCurrency(totalScheduled)}
          </div>
          <div className="text-slate-400 text-xs mt-1">{upcomingPayouts?.length} scheduled</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="text-slate-400 text-xs mb-1">Next Payout</div>
          {upcomingPayouts?.[0] ? (
            <>
              <div className="text-[#1a2744] text-2xl font-bold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {formatCurrency(upcomingPayouts?.[0]?.amount)}
              </div>
              <div className="text-slate-400 text-xs mt-1 flex items-center gap-1">
                <Calendar size={10} />
                {formatDate(upcomingPayouts?.[0]?.payout_date)}
                {getDaysUntil(upcomingPayouts?.[0]?.payout_date) !== null && (
                  <span className="text-blue-500 font-medium">
                    · in {getDaysUntil(upcomingPayouts?.[0]?.payout_date)} days
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="text-slate-400 text-sm">No upcoming payouts</div>
          )}
        </div>
      </div>
      {/* Upcoming Scheduled */}
      {upcomingPayouts?.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6">
          <h3 className="text-[#1a2744] font-semibold text-sm mb-3 flex items-center gap-2">
            <Clock size={14} className="text-blue-500" />
            Upcoming Distributions
          </h3>
          <div className="space-y-2">
            {upcomingPayouts?.map((p) => {
              const days = getDaysUntil(p?.payout_date);
              return (
                <div key={p?.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Banknote size={14} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#1a2744]">{formatCurrency(p?.amount)}</div>
                      <div className="text-xs text-slate-400">{formatDate(p?.payout_date)}</div>
                    </div>
                  </div>
                  {days !== null && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${days <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {days === 0 ? 'Today' : `${days}d`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* History Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-[#1a2744] text-sm">Payment History</h3>
          <div className="flex gap-2">
            {['all', 'paid', 'scheduled', 'processing', 'failed']?.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all capitalize ${
                  filter === f ? 'bg-[#1a2744] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {filteredPayouts?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-6 py-3">
                    <SortButton field="payout_date" label="Date" />
                  </th>
                  <th className="text-left px-6 py-3">
                    <SortButton field="amount" label="Amount" />
                  </th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Method</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Reference</th>
                  <th className="text-left px-6 py-3">
                    <SortButton field="status" label="Status" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredPayouts?.map((p) => {
                  const cfg = statusConfig?.[p?.status] || statusConfig?.scheduled;
                  const Icon = cfg?.icon;
                  return (
                    <tr key={p?.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-600">{formatDate(p?.payout_date)}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#1a2744]">{formatCurrency(p?.amount)}</td>
                      <td className="px-6 py-4 text-sm text-slate-500 capitalize">{p?.payment_method?.replace('_', ' ') || 'Bank Transfer'}</td>
                      <td className="px-6 py-4 text-xs text-slate-400 font-mono">{p?.payment_reference || '—'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cfg?.bg} ${cfg?.color}`}>
                          <Icon size={10} />
                          {cfg?.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Banknote size={20} className="text-slate-400" />
            </div>
            <p className="text-slate-400 text-sm">No payout records found</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PayoutTracking;
